'use client';

import * as Dialog from '@radix-ui/react-dialog';
import styled from 'styled-components';
import { useScopedI18n } from '@locales/client';
import { Button } from '@/shared/ui/form/button/button';
import { Image } from '@/shared/ui/image/Image';
import {
  BodyTextMediumB,
  BodyTextRegularC,
  HeadingMediumH5,
} from '@/shared/ui/typography2';
import { createDialogCloseOnlyHandler } from '@/shared/libs/dialog-open-change';

interface UpdatePopupProps {
  onClose: () => void;
  onReinstall: () => void;
  reinstallDisabled?: boolean;
}

export const UpdatePopup = ({
  onClose,
  onReinstall,
  reinstallDisabled = false,
}: UpdatePopupProps) => {
  const t = useScopedI18n('application');

  return (
    <Dialog.Root
      open
      onOpenChange={createDialogCloseOnlyHandler(onClose)}
    >
      <Dialog.Portal>
        <DialogOverlay />
        <DialogContent aria-describedby="" data-item="app_update">
          <Dialog.Close asChild>
            <CloseButton
              type="button"
              aria-label={t('update_popup_close_aria')}
            >
              <CloseIconImage
                src="/images/icons/close-dialog.svg"
                width={32}
                height={32}
                alt=""
              />
            </CloseButton>
          </Dialog.Close>

          <TextContainer>
            <DialogTitle>{t('update_popup_title')}</DialogTitle>
            <DialogDescription>{t('update_popup_text')}</DialogDescription>
          </TextContainer>

          <ReinstallButton
            type="button"
            onClick={onReinstall}
            disabled={reinstallDisabled}
            fill
            data-analytics-id="app_update"
          >
            <BodyTextMediumB>{t('update_popup_button')}</BodyTextMediumB>
          </ReinstallButton>
        </DialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const DialogOverlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
`;

const DialogContent = styled(Dialog.Content)`
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 1001;
  display: flex;
  width: min(400px, calc(100vw - 32px));
  max-height: calc(
    100vh - var(--app-safe-area-top) - var(--app-safe-area-bottom) - 32px
  );
  max-height: calc(
    100dvh - var(--app-safe-area-top) - var(--app-safe-area-bottom) - 32px
  );
  padding: 32px 24px 24px;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  transform: translate(-50%, -50%);
  border-radius: 12px;
  background: rgba(19, 19, 21, 0.96);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
  color: #edeff5;

  &:focus {
    outline: none;
  }

  @media (orientation: landscape) and (max-height: 420px) {
    gap: 16px;
    padding-top: 24px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #edeff5;
    outline-offset: 2px;
  }
`;

const CloseIconImage = styled(Image)`
  display: block;

  img {
    display: block;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  text-align: center;
`;

const DialogTitle = styled(Dialog.Title)`
  ${HeadingMediumH5};
`;

const DialogDescription = styled(Dialog.Description)`
  ${BodyTextRegularC};
  color: #afafaf;
`;

const ReinstallButton = styled(Button)`
  min-height: 48px;
  flex: none;
`;
